import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, catchError, concatMap, EMPTY, Observable, Subject, tap } from 'rxjs';
import { Constants } from 'projects/borrower-app/src/app/services/constants';
import { DataService } from 'projects/borrower-app/src/app/services/data.service';
import {
  asLang,
  decideOnApplicationLoad,
  LEGACY_MIGRATED_KEY,
  parseLegacyMigrationOwner,
  PENDING_LANG_KEY,
  PendingChoice,
  PendingChoices,
  PRE_APPLICATION_KEY,
  PREFERRED_LANG_KEY,
  signInIdentity,
  UiLang,
} from './ui-language';

const url = (applicationId: number) => `api/BorrowerPortal/applications/${applicationId}/ui-language`;

export interface LoadedApplication {
  applicationId: number;
  /** signInIdentity of the sign-in that loaded it. */
  session: string;
}

interface SaveRequest {
  applicationId: number;
  lang: UiLang;
  /** Identity of the sign-in that made the choice; the save is dropped if someone else is signed in when it runs. */
  session: string;
  /** Pending version this save acknowledges (null for the release migration, which has no pending record). */
  version: number | null;
  completesMigration: boolean;
}

/**
 * Keeps the borrower-app display language in step with the application (LLS-886 §2): saved per
 * borrower per application on the server, where co-borrowers inherit the primary borrower's choice.
 * localStorage stays the boot hint; the server value wins once an application is known, unless this tab
 * holds an unsaved choice for it, which is then saved.
 *
 * Unsaved choices are records in this tab's sessionStorage (so they belong to the person in this tab),
 * keyed by application (or "pre" before one is loaded; an in-app choice supersedes it), bound to the sign-in
 * that made them (borrower + company, stable across token refresh) and versioned. Saves run one at a time, only for the sign-in that queued them, and clear their record only
 * if it is still the same version. No auth token means no application: after logout, idle timeout or a
 * 401 the service makes no calls (public pages stay anonymous).
 *
 * Release migration: a browser that already had Spanish chosen before this feature carries that choice over
 * to the first application loaded, so borrowers mid-application are not switched back to English. The
 * migration is claimed for that application and sign-in and retried only for them until its save succeeds.
 */
@Injectable({ providedIn: 'root' })
export class UiLanguageService {
  private _applicationId: number | null = null;
  private _session: string | null = null; // the sign-in that loaded _applicationId
  private _userChoseSinceLoad = false;
  private readonly _saves = new Subject<SaveRequest>();
  private readonly _application = new BehaviorSubject<LoadedApplication | null>(null);

  /** The application loaded for the signed-in borrower (null when none or signed out) — LLS-886 §5 gate input. */
  readonly application$: Observable<LoadedApplication | null> = this._application.asObservable();

  constructor(
    private readonly _dataService: DataService,
    private readonly _transloco: TranslocoService,
    private readonly _window: Window,
  ) {
    this._saves.pipe(
      concatMap(request => {
        if (this.currentSession() !== request.session) {
          return EMPTY; // queued by a different sign-in; its record stays pending for that sign-in
        }
        return this._dataService.put(url(request.applicationId), { languageCode: request.lang }).pipe(
          tap(() => this.onSaved(request)),
          catchError(() => EMPTY), // best effort; the record stays pending and is retried on the next load
        );
      }),
    ).subscribe();
  }

  setApplication(applicationId: number | null | undefined): void {
    const session = this.currentSession();
    if (!session) {
      this.forgetApplication();
      return;
    }
    const id = applicationId && applicationId > 0 ? applicationId : null;
    if (id === this._applicationId && session === this._session) {
      return;
    }
    this._applicationId = id;
    this._session = session;
    this._userChoseSinceLoad = false;
    this._application.next(id ? { applicationId: id, session } : null);
    if (!id) {
      return;
    }
    this._dataService.get(url(id)).subscribe({
      next: (res: { languageCode: string | null } | null) => {
        if (id !== this._applicationId || session !== this._session || session !== this.currentSession()) {
          return; // stale: another application or another sign-in has been loaded since
        }
        if (this._userChoseSinceLoad) {
          return; // an in-page choice supersedes this response and is being saved
        }
        this.applyOnLoad(id, session, res?.languageCode ?? null);
      },
      error: () => { /* keep the device language; nothing to undo */ },
    });
  }

  /** Called by the language switcher after the user picks a language. */
  recordUserChoice(value: string): void {
    const lang = asLang(value);
    if (!lang) {
      return;
    }
    this._userChoseSinceLoad = true;
    const version = this.nextVersion();
    const session = this.currentSession();
    if (this._applicationId && session && session === this._session) {
      this.setPending(String(this._applicationId), { lang, version, session });
      this.deletePending(PRE_APPLICATION_KEY); // superseded: must not be re-applied on a later load
      this._saves.next({ applicationId: this._applicationId, lang, session, version, completesMigration: false });
    } else {
      this.forgetApplication();
      this.setPending(PRE_APPLICATION_KEY, { lang, version, session: null });
    }
  }

  private applyOnLoad(id: number, session: string, serverLang: string | null): void {
    const key = String(id);
    let own: PendingChoice | null = this.getPending(key);
    if (own && own.session !== session) {
      own = null; // another sign-in's unsaved choice for this application — not ours to apply
    }
    // The newest applicable choice wins: this sign-in's record for the application, or one made before
    // an application was loaded (e.g. on the login page after a logout).
    const pre = this.getPending(PRE_APPLICATION_KEY);
    const pending = pre && (!own || pre.version > own.version) ? pre : own;

    const legacyLang = this.legacyMigrationFor(id, session);

    const decision = decideOnApplicationLoad({
      serverLang,
      localLang: this.read(PREFERRED_LANG_KEY),
      pendingLang: pending?.lang ?? legacyLang,
    });
    if (decision.apply) {
      this._transloco.setActiveLang(decision.apply);
      this.write(PREFERRED_LANG_KEY, decision.apply);
    }
    if (!decision.persist) {
      return;
    }
    if (pending) {
      // The chosen record now belongs to this application and sign-in; a pre-application record is consumed.
      this.deletePending(PRE_APPLICATION_KEY);
      const owned: PendingChoice = { ...pending, session };
      this.setPending(key, owned);
      this._saves.next({ applicationId: id, lang: owned.lang, session, version: owned.version, completesMigration: !!legacyLang });
    } else {
      this._saves.next({ applicationId: id, lang: decision.persist, session, version: null, completesMigration: true });
    }
  }

  /**
   * The pre-release language to carry over to this application, if the release migration is (still) this
   * application's: claimed by the first application loaded, then retried only for it and its sign-in.
   */
  private legacyMigrationFor(applicationId: number, identity: string): UiLang | null {
    const marker = this.read(LEGACY_MIGRATED_KEY);
    if (marker === '1') {
      return null;
    }
    if (marker) {
      const owner = parseLegacyMigrationOwner(marker);
      return owner && owner.applicationId === applicationId && owner.identity === identity ? owner.lang : null;
    }
    if (this.read(PREFERRED_LANG_KEY) !== 'es') {
      this.write(LEGACY_MIGRATED_KEY, '1'); // nothing to carry over in this browser
      return null;
    }
    this.write(LEGACY_MIGRATED_KEY, JSON.stringify({ applicationId, lang: 'es', identity }));
    return 'es';
  }

  private onSaved(request: SaveRequest): void {
    const key = String(request.applicationId);
    const pending = this.getPending(key);
    if (request.version !== null && pending && pending.version === request.version) {
      this.deletePending(key); // only the choice this save carried; a newer one stays pending
    }
    if (request.completesMigration || this.ownsLegacyMigration(request.applicationId, request.session)) {
      this.write(LEGACY_MIGRATED_KEY, '1'); // done, or superseded by a newer explicit choice now saved
    }
  }

  private ownsLegacyMigration(applicationId: number, identity: string): boolean {
    const owner = parseLegacyMigrationOwner(this.read(LEGACY_MIGRATED_KEY) ?? '');
    return !!owner && owner.applicationId === applicationId && owner.identity === identity;
  }

  private forgetApplication(): void {
    this._applicationId = null;
    this._session = null;
    this._application.next(null);
  }

  /** Identity of the current sign-in (see signInIdentity), or null when signed out. */
  private currentSession(): string | null {
    return signInIdentity(this.read(Constants.authorization.authorizationDataKey));
  }

  private nextVersion(): number {
    const versions = Object.values(this.getAllPending()).map(p => p.version);
    return Math.max(Date.now(), ...versions.map(v => v + 1));
  }

  private getAllPending(): PendingChoices {
    try {
      const parsed = JSON.parse(this._window.sessionStorage.getItem(PENDING_LANG_KEY) ?? '{}');
      return parsed && typeof parsed === 'object' ? parsed as PendingChoices : {};
    } catch {
      return {};
    }
  }

  private getPending(key: string): PendingChoice | null {
    const choice = this.getAllPending()[key];
    return choice && asLang(choice.lang) ? choice : null;
  }

  private setPending(key: string, choice: PendingChoice): void {
    this.savePending({ ...this.getAllPending(), [key]: choice });
  }

  private deletePending(key: string): void {
    const all = this.getAllPending();
    delete all[key];
    this.savePending(all);
  }

  private savePending(all: PendingChoices): void {
    try {
      if (Object.keys(all).length) {
        this._window.sessionStorage.setItem(PENDING_LANG_KEY, JSON.stringify(all));
      } else {
        this._window.sessionStorage.removeItem(PENDING_LANG_KEY);
      }
    } catch { /* ignore */ }
  }

  private read(key: string): string | null {
    try { return this._window.localStorage.getItem(key); } catch { return null; }
  }

  private write(key: string, value: string): void {
    try { this._window.localStorage.setItem(key, value); } catch { /* ignore */ }
  }
}
